// ** React Imports
import { SetStateAction, useEffect, useState } from 'react';

// ** MUI Imports
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Card from '@mui/material/Card';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import { styled } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import CardContent from '@mui/material/CardContent';
import CardActions from '@mui/material/CardActions';
import Tooltip from '@mui/material/Tooltip';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Link from '@mui/material/Link';
import CloseIcon from '@mui/icons-material/Close';

// ** Custom Components
import CustomChip from 'src/materio/@core/components/mui/chip';

// ** Types
import { ThemeColor } from 'src/materio/@core/layouts/types';

import {
    CadenceSelectionDialogStore,
    DeleteLeadStore,
    InboundCallStore,
    Lead,
    LeadOverrideStore,
    LeadScreenStore,
    QuickLeadDialogStore,
    ViewType,
} from 'src/stores/LeadStore';
import Icon from 'src/features/common/CustomIcon';
import { LeadDataFormater } from 'src/features/leads/view/LeadDataFormater';
import { LeadAccountComponent } from 'src/features/leads/view/LeadAccountComponent';
import { LeadCompanyComponent } from 'src/features/leads/view/LeadCompanyComponent';
import LocalFireDepartmentTwoTone from '@mui/icons-material/LocalFireDepartmentTwoTone';
import PhoneIphoneTwoToneIcon from '@mui/icons-material/PhoneIphoneTwoTone';
import LocalPhoneTwoToneIcon from '@mui/icons-material/LocalPhoneTwoTone';
import SupportAgentTwoToneIcon from '@mui/icons-material/SupportAgentTwoTone';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import EmailTwoToneIcon from '@mui/icons-material/EmailTwoTone';
import { nextDateDays } from 'src/utils/date-utils';
import { nextDateDaysTooltip } from 'src/utils/date-utils';
import { previousDateDaysTooltip } from 'src/utils/date-utils';
import { previousDateDays } from 'src/utils/date-utils';
import { colorDateTooltipOverdue } from 'src/utils/date-utils';
import { ActivityReady } from 'src/utils/date-utils';
import { ActivityColor } from 'src/utils/date-utils';
import QuestionAnswerTwoToneIcon from '@mui/icons-material/QuestionAnswerTwoTone';
import LanguageIcon from '@mui/icons-material/Language';
import FaxIcon from '@mui/icons-material/Fax';
import EditIcon from '@mui/icons-material/Edit';
import NoteIcon from '@mui/icons-material/Note';
import AnalyticsIcon from '@mui/icons-material/Analytics';
import IconButton from '@mui/material/IconButton';
import ChatIcon from '@mui/icons-material/Chat';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import GroupsIcon from '@mui/icons-material/Groups';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import ActivityDialog from '../../leads/view/ActivityDialog';
import { client } from '../../../utils/axios-client';
import { loadUserAvatarEndpoint } from '../../../configs/endpoints';
import PhoneForwardedIcon from '@mui/icons-material/PhoneForwarded';
import Avatar from '@mui/material/Avatar';
import HotLeadToggleIcon from '../../common/HotLeadToggleIcon';
import AddLeadToCadence from './AddLeadToCadence';
import ContactMailTwoToneIcon from '@mui/icons-material/ContactMailTwoTone';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import DisplaySettingsIcon from '@mui/icons-material/DisplaySettings';
import ChangeCircleIcon from '@mui/icons-material/ChangeCircle';
import ManageHistoryIcon from '@mui/icons-material/ManageHistory';
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';
import DeleteTwoToneIcon from '@mui/icons-material/DeleteTwoTone';
import DateRangeIcon from '@mui/icons-material/DateRange';
import RenderIf, { ClaimType, ClaimValue } from '../../common/RenderIf';
import { isEmpty } from 'lodash';
import CloseButton from '../../../v2/components/Button/CloseButton';

interface Props {
    item: Lead;
    onClose?: () => void;
}

interface ColorsType {
    [key: string]: ThemeColor;
}

// ** Styled <sup> component
const Sup = styled('sup')(({ theme }) => ({
    top: '0.2rem',
    left: '-0.6rem',
    position: 'absolute',
    color: theme.palette.primary.main,
}));

// ** Styled <sub> component
const Sub = styled('sub')({
    fontWeight: 300,
    fontSize: '1rem',
    alignSelf: 'flex-end',
});

const roleColors: ColorsType = {
    admin: 'error',
    editor: 'info',
    author: 'warning',
    maintainer: 'success',
    subscriber: 'primary',
};

const statusColors: ColorsType = {
    active: 'success',
    pending: 'warning',
    inactive: 'secondary',
};

const LeadSummaryView = ({ item, onClose }: Props) => {
    //const selectedLead = LeadScreenStore.useState((s) => s.selectedLead);
    const selectedView = LeadScreenStore.useState((s) => s.selectedView);
    const [imgSrc, setImgSrc] = useState<string>('');
    const [cadenceSelectionDialogMode, setCadenceSelectionDialogMode] = useState(false);
    const quickLeadDialogMode = QuickLeadDialogStore.useState((s) => s.quickLeadDialogMode);

    const onEditLead = () => {
        LeadScreenStore.update((s) => {
            s.selectedLead = item;
            s.selectedView = ViewType.Edit;
            s.editMode = true;
        });
    };

    const onExecuteActivity = () => {
        InboundCallStore.update((s) => {
            s.showPhoneControls = false;
        });
        LeadScreenStore.update((s) => {
            s.selectedLead = item;
            s.selectedView = ViewType.DoActivity;
            s.editMode = false;
        });
    };

    const onCloseAddLeadToCadence = () => {
        setCadenceSelectionDialogMode(false);
    };

    const onAddLeadToCadence = (id: string) => {
        setCadenceSelectionDialogMode(true);
    };

    useEffect(() => {
        const loadImage = async () => {
            if (!isEmpty(item.owner.avatarFileName)) {
                const response = await client.get(loadUserAvatarEndpoint.replace('{id}', item.owner.avatarFileName));
                setImgSrc(response.data);
            }
        };

        if (item.owner) loadImage();
    }, []);

    const [anchorEl, setAnchorEl] = useState<Element | null>(null);

    const open = Boolean(anchorEl);

    const handleClick = (event: React.MouseEvent) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = (actionType: string) => {
        LeadScreenStore.update((s) => {
            (s.selectedLead = item), (s.selectedView = ViewType.None);
        });
        switch (actionType) {
            case 'ACTIVITY':
                LeadOverrideStore.update((s) => {
                    s.changeActivityMode = true;
                });
                LeadOverrideStore.update((s) => {
                    s.changeCadenceMode = false;
                });
                LeadOverrideStore.update((s) => {
                    s.changeSprintMode = false;
                });
                LeadOverrideStore.update((s) => {
                    s.changeOwnerMode = false;
                });
                LeadOverrideStore.update((s) => {
                    s.changeDateMode = false;
                });
                break;
            case 'CADENCE':
                LeadOverrideStore.update((s) => {
                    s.changeActivityMode = false;
                });
                LeadOverrideStore.update((s) => {
                    s.changeCadenceMode = true;
                });
                LeadOverrideStore.update((s) => {
                    s.changeSprintMode = false;
                });
                LeadOverrideStore.update((s) => {
                    s.changeOwnerMode = false;
                });
                LeadOverrideStore.update((s) => {
                    s.changeDateMode = false;
                });
                break;
            case 'SPRINT':
                LeadOverrideStore.update((s) => {
                    s.changeActivityMode = false;
                });
                LeadOverrideStore.update((s) => {
                    s.changeCadenceMode = false;
                });
                LeadOverrideStore.update((s) => {
                    s.changeSprintMode = true;
                });
                LeadOverrideStore.update((s) => {
                    s.changeOwnerMode = false;
                });
                LeadOverrideStore.update((s) => {
                    s.changeDateMode = false;
                });
                break;
            case 'OWNER':
                LeadOverrideStore.update((s) => {
                    s.changeActivityMode = false;
                });
                LeadOverrideStore.update((s) => {
                    s.changeCadenceMode = false;
                });
                LeadOverrideStore.update((s) => {
                    s.changeSprintMode = false;
                });
                LeadOverrideStore.update((s) => {
                    s.changeOwnerMode = true;
                });
                LeadOverrideStore.update((s) => {
                    s.changeDateMode = false;
                });
                break;
            case 'DATE':
                LeadOverrideStore.update((s) => {
                    s.changeActivityMode = false;
                });
                LeadOverrideStore.update((s) => {
                    s.changeCadenceMode = false;
                });
                LeadOverrideStore.update((s) => {
                    s.changeSprintMode = false;
                });
                LeadOverrideStore.update((s) => {
                    s.changeOwnerMode = false;
                });
                LeadOverrideStore.update((s) => {
                    s.changeDateMode = true;
                });
                break;
            default:
        }
        setAnchorEl(null);
    };

    const onDeleteLead = () => {
        LeadScreenStore.update((s) => {
            (s.selectedLead = item), (s.selectedView = ViewType.None);
        });
        DeleteLeadStore.update((s) => {
            s.deleteLead = true;
        });
    };

    if (item) {
        return (
            <Box>
                <Grid container spacing={6}>
                    <Grid item xs={12}>
                        <Card>
                            <CardContent sx={{ pt: 1, display: 'flex', alignItems: 'center', flexDirection: 'column' }}>
                                <Grid container spacing={2} justifyContent={'center'} alignItems={'center'}>
                                    <Grid
                                        item
                                        xs={12}
                                        sx={{
                                            pt: 1,
                                            position: 'relative',
                                            display: 'flex',
                                            justifyContent: 'center',
                                            alignItems: 'center',
                                        }}
                                    >
                                        <Typography variant="h6">
                                            {item.firstName} {item.lastName}
                                        </Typography>

                                        <CloseButton
                                            variant={'text'}
                                            onClick={onClose}
                                            sx={{ position: 'absolute', right: 0 }} // Positions the button at the far right
                                        ></CloseButton>
                                    </Grid>

                                    <Grid
                                        item
                                        xs={12}
                                        justifyContent={'center'}
                                        alignItems={'center'}
                                        sx={{ pt: 1, display: 'flex', alignItems: 'center', flexDirection: 'column' }}
                                    >
                                        <LeadDataFormater
                                            labelText=""
                                            valueText={item.jobTitle}
                                            textColor="CornflowerBlue"
                                            errorText="-Job Title is missing-"
                                            chipHeight={20}
                                            chipFontSize="0.875rem"
                                            chipfontWeight={500}
                                            isChip={false}
                                        />
                                        <LeadCompanyComponent
                                            lead={item}
                                            labelText="at"
                                            valueText={item.company != null ? item.company : ''}
                                            textColor="CornflowerBlue"
                                            errorText="-Company is missing-"
                                            chipHeight={20}
                                            chipFontSize="0.75rem"
                                            chipfontWeight={500}
                                            isChip={false}
                                            leadId="1"
                                        />
                                    </Grid>
                                </Grid>
                            </CardContent>
                            <CardContent>
                                <Grid container spacing={2} justifyContent={'center'}>
                                    <Grid item xs={6}>
                                        <Box sx={{ display: 'flex', mb: 0.7, alignItems: 'center' }}>
                                            <LeadDataFormater
                                                labelText="Stage:"
                                                valueText={item.leadStage != null ? item.leadStage.name : ''}
                                                textColor={item.leadStage != null ? item.leadStage.stageColor : ''}
                                                errorText="-Stage is missing-"
                                                chipHeight={20}
                                                chipFontSize="0.875rem"
                                                chipfontWeight={500}
                                                isChip={true}
                                            />
                                        </Box>
                                    </Grid>
                                    <Grid item xs={6}>
                                        <Box sx={{ display: 'flex', mb: 0.7, alignItems: 'center' }}>
                                            <LeadAccountComponent
                                                lead={item}
                                                labelText="Account:"
                                                valueText={item.account != null ? item.account.name : ''}
                                                textColor=""
                                                errorText="-Account is missing-"
                                                chipHeight={20}
                                                chipFontSize="0.75rem"
                                                chipfontWeight={500}
                                                isChip={false}
                                                leadId="1"
                                            />
                                        </Box>
                                    </Grid>
                                </Grid>
                                <Divider />
                                <Box>
                                    <Grid container spacing={2} justifyContent={'center'}>
                                        <Grid
                                            item
                                            xs={12}
                                            md={10}
                                            justifyContent="center"
                                            sx={{ display: 'flex', alignItems: 'left', flexDirection: 'column' }}
                                        >
                                            <Box sx={{ display: 'flex', alignItems: 'left' }}>
                                                {item.mobilePhone != null && item.mobilePhone != '' && !item.dncMobile && (
                                                    <Tooltip title={'Cell/Mobile: ' + item.mobilePhone}>
                                                        <PhoneIphoneTwoToneIcon color="action" />
                                                    </Tooltip>
                                                )}
                                                {item.mobilePhone != null && item.mobilePhone != '' && item.dncMobile && (
                                                    <Tooltip title={'Cell/Mobile: ' + item.mobilePhone + ' (DNC)'}>
                                                        <PhoneIphoneTwoToneIcon color="warning" />
                                                    </Tooltip>
                                                )}

                                                {item.directPhone != null && item.directPhone != '' && !item.dncPhone && (
                                                    <Tooltip title={'Direct phone: ' + item.directPhone}>
                                                        <LocalPhoneTwoToneIcon color="action" />
                                                    </Tooltip>
                                                )}
                                                {item.directPhone != null && item.directPhone != '' && item.dncPhone && (
                                                    <Tooltip title={'Direct phone: ' + item.directPhone + ' (DNC)'}>
                                                        <LocalPhoneTwoToneIcon color="warning" />
                                                    </Tooltip>
                                                )}
                                                {item.switchboardPhone != null &&
                                                    item.switchboardPhone != '' &&
                                                    !item.dncSwitchboard && (
                                                        <Tooltip title={'Switchboard: ' + item.switchboardPhone}>
                                                            <SupportAgentTwoToneIcon color="action" />
                                                        </Tooltip>
                                                    )}
                                                {item.switchboardPhone != null &&
                                                    item.switchboardPhone != '' &&
                                                    item.dncSwitchboard && (
                                                        <Tooltip
                                                            title={'Switchboard: ' + item.switchboardPhone + ' (DNC)'}
                                                        >
                                                            <SupportAgentTwoToneIcon color="warning" />
                                                        </Tooltip>
                                                    )}
                                                {item.linkedIn != null && item.linkedIn != '' && (
                                                    <a href={item.linkedIn} target="_blank">
                                                        <Tooltip title={'LinkedIn: ' + item.linkedIn}>
                                                            <LinkedInIcon color="action" />
                                                        </Tooltip>
                                                    </a>
                                                )}
                                                {item.emailAddress != null && item.emailAddress != '' && (
                                                    <Tooltip title={'Email: ' + item.emailAddress}>
                                                        <EmailTwoToneIcon color="action" />
                                                    </Tooltip>
                                                )}
                                                {item.switchboardEmail != null && item.switchboardEmail != '' && (
                                                    <Tooltip title={'Switchboard Email: ' + item.switchboardEmail}>
                                                        <ContactMailTwoToneIcon color="action" />
                                                    </Tooltip>
                                                )}
                                                {item.website != null && item.website != '' && (
                                                    <Tooltip title={'Web: ' + item.website}>
                                                        <LanguageIcon color="action" />
                                                    </Tooltip>
                                                )}
                                                {item.fax != null && item.fax != '' && (
                                                    <Tooltip title={'Fax: ' + item.fax}>
                                                        <FaxIcon color="action" />
                                                    </Tooltip>
                                                )}
                                            </Box>
                                        </Grid>
                                        <Grid
                                            item
                                            xs={12}
                                            md={1}
                                            justifyContent="center"
                                            sx={{ display: 'flex', alignItems: 'right', flexDirection: 'column' }}
                                        >
                                            <Box sx={{ display: 'flex', alignItems: 'right' }}>
                                                <HotLeadToggleIcon leadId={item.id} hotLead={item.hotLead} />
                                            </Box>
                                        </Grid>
                                        <Grid
                                            item
                                            xs={10}
                                            md={1}
                                            justifyContent="center"
                                            sx={{ display: 'flex', alignItems: 'left', flexDirection: 'column' }}
                                        >
                                            <Box sx={{ display: 'flex', alignItems: 'left' }}>
                                                {item.meaningfulInteraction && (
                                                    <Tooltip title="Has a Meaningful Interaction">
                                                        <QuestionAnswerTwoToneIcon sx={{ color: 'LightSeaGreen' }} />
                                                    </Tooltip>
                                                )}
                                            </Box>
                                        </Grid>
                                    </Grid>
                                </Box>
                                <Divider />
                                <Box>
                                    <Grid container spacing={2} justifyContent={'center'}>
                                        <Grid item xs={12}>
                                            <Box sx={{ display: 'flex', mb: 0.7, alignItems: 'center' }}>
                                                {item.lastActivity &&
                                                    item.lastActivity.name != '' &&
                                                    item.lastActivityDateTime && (
                                                        <Tooltip
                                                            title={previousDateDaysTooltip(item.lastActivityDateTime)}
                                                        >
                                                            <Typography variant="caption" sx={{ fontStyle: 'italic' }}>
                                                                Last Activity: {item.lastActivity.name}
                                                                {previousDateDays(item.lastActivityDateTime)} by{' '}
                                                                {item.lastActivityOwner.fullName}
                                                            </Typography>
                                                        </Tooltip>
                                                    )}
                                                <RenderIf
                                                    claimType={ClaimType.Leads}
                                                    claimValue={ClaimValue.ViewActivityHistory}
                                                >
                                                    {item.lastActivity &&
                                                        item.lastActivity.name != '' &&
                                                        item.lastActivityDateTime &&
                                                        !quickLeadDialogMode && (
                                                            <Box
                                                                sx={{ display: 'flex', ml: 2.7, alignItems: 'center' }}
                                                            >
                                                                <Link
                                                                    sx={{ fontStyle: 'italic' }}
                                                                    underline="hover"
                                                                    component="button"
                                                                    variant="caption"
                                                                    onClick={() =>
                                                                        LeadScreenStore.update((s) => {
                                                                            s.selectedView = ViewType.ActivityTimeline;
                                                                            s.selectedLead = item;
                                                                        })
                                                                    }
                                                                >
                                                                    view history
                                                                </Link>
                                                            </Box>
                                                        )}
                                                </RenderIf>

                                                {!item.lastActivity && (
                                                    <Typography variant="caption">No previous activity</Typography>
                                                )}
                                            </Box>
                                        </Grid>
                                        <Grid item xs={12}>
                                            <Box sx={{ display: 'flex', mb: 0.7, alignItems: 'center' }}>
                                                {item.nextActivity &&
                                                    item.nextActivity.name != '' &&
                                                    item.nextActivityDateTime && (
                                                        <Tooltip title={nextDateDaysTooltip(item.nextActivityDateTime)}>
                                                            <Typography
                                                                variant="caption"
                                                                sx={{
                                                                    fontStyle: 'italic',
                                                                    color: colorDateTooltipOverdue(
                                                                        item.nextActivityDateTime,
                                                                    ),
                                                                }}
                                                            >
                                                                Next Activity: {item.nextActivity.name}
                                                                {nextDateDays(item.nextActivityDateTime)}
                                                            </Typography>
                                                        </Tooltip>
                                                    )}
                                                <RenderIf
                                                    claimType={ClaimType.Leads}
                                                    claimValue={ClaimValue.ViewCadence}
                                                >
                                                    {item.nextActivity &&
                                                        item.nextActivity.name != '' &&
                                                        item.nextActivityDateTime &&
                                                        !quickLeadDialogMode && (
                                                            <Box
                                                                sx={{ display: 'flex', ml: 2.7, alignItems: 'center' }}
                                                            >
                                                                <Link
                                                                    sx={{ fontStyle: 'italic' }}
                                                                    underline="hover"
                                                                    component="button"
                                                                    variant="caption"
                                                                    onClick={() =>
                                                                        LeadScreenStore.update((s) => {
                                                                            s.selectedView = ViewType.ViewCadence;
                                                                            s.selectedLead = item;
                                                                        })
                                                                    }
                                                                >
                                                                    view cadence
                                                                </Link>
                                                            </Box>
                                                        )}
                                                </RenderIf>
                                                {!item.nextActivity && (
                                                    <Typography variant="caption">No next activity</Typography>
                                                )}
                                                <RenderIf
                                                    claimType={ClaimType.Leads}
                                                    claimValue={ClaimValue.AddToCadence}
                                                >
                                                    {!item.nextActivity &&
                                                        !cadenceSelectionDialogMode &&
                                                        !quickLeadDialogMode && (
                                                            <Box
                                                                sx={{ display: 'flex', ml: 2.7, alignItems: 'center' }}
                                                            >
                                                                <Link
                                                                    sx={{ fontStyle: 'italic' }}
                                                                    underline="hover"
                                                                    component="button"
                                                                    variant="caption"
                                                                    onClick={() => onAddLeadToCadence(item.id)}
                                                                >
                                                                    add lead to cadence
                                                                </Link>
                                                            </Box>
                                                        )}
                                                </RenderIf>
                                            </Box>
                                        </Grid>
                                        {cadenceSelectionDialogMode && !item.nextActivity && !quickLeadDialogMode && (
                                            <Grid item xs={12}>
                                                <AddLeadToCadence leadId={item.id} onClose={onCloseAddLeadToCadence} />
                                            </Grid>
                                        )}
                                        {item.sprint && (
                                            <Grid item xs={12}>
                                                <Box sx={{ display: 'flex', mb: 0.7, alignItems: 'center' }}>
                                                    <Tooltip title="Sprint">
                                                        <Typography variant="caption" sx={{ fontStyle: 'italic' }}>
                                                            Sprint: {item.sprint.name}
                                                        </Typography>
                                                    </Tooltip>
                                                    <RenderIf
                                                        claimType={ClaimType.Leads}
                                                        claimValue={ClaimValue.ViewSprint}
                                                    >
                                                        {!quickLeadDialogMode && (
                                                            <Box
                                                                sx={{ display: 'flex', ml: 2.7, alignItems: 'center' }}
                                                            >
                                                                <Link
                                                                    sx={{ fontStyle: 'italic' }}
                                                                    underline="hover"
                                                                    component="button"
                                                                    variant="caption"
                                                                    onClick={() =>
                                                                        LeadScreenStore.update((s) => {
                                                                            s.selectedView = ViewType.ViewSprint;
                                                                            s.selectedLead = item;
                                                                        })
                                                                    }
                                                                >
                                                                    view sprint
                                                                </Link>
                                                            </Box>
                                                        )}
                                                    </RenderIf>
                                                </Box>
                                            </Grid>
                                        )}
                                        {!item.sprint && !quickLeadDialogMode && (
                                            <Grid item xs={12}>
                                                <Box sx={{ display: 'flex', mb: 0.7, alignItems: 'center' }}>
                                                    <Tooltip title="Sprint">
                                                        <Typography variant="caption" sx={{ fontStyle: 'italic' }}>
                                                            Not assigned to a sprint
                                                        </Typography>
                                                    </Tooltip>
                                                    <RenderIf
                                                        claimType={ClaimType.Leads}
                                                        claimValue={ClaimValue.AddToSprint}
                                                    >
                                                        <Box sx={{ display: 'flex', ml: 2.7, alignItems: 'center' }}>
                                                            <Link
                                                                sx={{ fontStyle: 'italic' }}
                                                                underline="hover"
                                                                component="button"
                                                                variant="caption"
                                                                onClick={() =>
                                                                    LeadScreenStore.update((s) => {
                                                                        s.selectedView = ViewType.ActivityTimeline;
                                                                        s.selectedLead = item;
                                                                    })
                                                                }
                                                            >
                                                                assign to sprint
                                                            </Link>
                                                        </Box>
                                                    </RenderIf>
                                                </Box>
                                            </Grid>
                                        )}
                                        <Grid item xs={12}>
                                            <Box sx={{ display: 'flex', mb: 0.7, alignItems: 'center' }}>
                                                {item.owner && item.owner.fullName != '' && (
                                                    <Grid container spacing={2} justifyContent={'center'}>
                                                        <Grid
                                                            item
                                                            xs={12}
                                                            md={1}
                                                            sx={{
                                                                pt: 1,
                                                                display: 'flex',
                                                                alignItems: 'left',
                                                                flexDirection: 'column',
                                                            }}
                                                        >
                                                            <Tooltip title="Primary lead owner">
                                                                <Avatar
                                                                    alt={item.owner.fullName}
                                                                    sx={{ width: 20, height: 20 }}
                                                                    src={imgSrc ? imgSrc : '/images/avatars/1.png'}
                                                                />
                                                            </Tooltip>
                                                        </Grid>
                                                        <Grid
                                                            item
                                                            xs={12}
                                                            md={11}
                                                            sx={{
                                                                pt: 1,
                                                                display: 'flex',
                                                                alignItems: 'left',
                                                                flexDirection: 'column',
                                                            }}
                                                        >
                                                            <Tooltip title="Primary lead owner">
                                                                <Typography
                                                                    variant="caption"
                                                                    sx={{ fontStyle: 'italic' }}
                                                                >
                                                                    Owner: {item.owner.fullName}
                                                                </Typography>
                                                            </Tooltip>
                                                        </Grid>
                                                    </Grid>
                                                )}
                                            </Box>
                                        </Grid>
                                    </Grid>
                                </Box>
                                <Accordion>
                                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                        <Typography variant="caption">Additional details...</Typography>
                                    </AccordionSummary>
                                    <AccordionDetails>
                                        <Box>
                                            <Grid container spacing={2} justifyContent={'center'}>
                                                <Grid item xs={6}>
                                                    <Box sx={{ display: 'flex', mb: 0.7, alignItems: 'center' }}>
                                                        <LeadDataFormater
                                                            labelText="Industry:"
                                                            valueText={item.industry != null ? item.industry.name : ''}
                                                            textColor=""
                                                            errorText="-Unknown-"
                                                            chipHeight={20}
                                                            chipFontSize="0.75rem"
                                                            chipfontWeight={500}
                                                            isChip={false}
                                                        />
                                                    </Box>
                                                </Grid>
                                                <Grid item xs={6}>
                                                    <Box sx={{ display: 'flex', mb: 0.7, alignItems: 'center' }}>
                                                        <LeadDataFormater
                                                            labelText="Level:"
                                                            valueText={
                                                                item.leadLevel != null ? item.leadLevel.name : ''
                                                            }
                                                            textColor=""
                                                            errorText="-Unknown-"
                                                            chipHeight={20}
                                                            chipFontSize="0.75rem"
                                                            chipfontWeight={500}
                                                            isChip={false}
                                                        />
                                                    </Box>
                                                </Grid>
                                                <Grid item xs={6}>
                                                    <Box sx={{ display: 'flex', mb: 0.7, alignItems: 'center' }}>
                                                        <LeadDataFormater
                                                            labelText="Function:"
                                                            valueText={
                                                                item.leadFunction != null ? item.leadFunction.name : ''
                                                            }
                                                            textColor=""
                                                            errorText="-Unknown-"
                                                            chipHeight={20}
                                                            chipFontSize="0.75rem"
                                                            chipfontWeight={500}
                                                            isChip={false}
                                                        />
                                                    </Box>
                                                </Grid>
                                                <Grid item xs={6}>
                                                    <Box sx={{ display: 'flex', mb: 0.7, alignItems: 'center' }}>
                                                        <LeadDataFormater
                                                            labelText="Source:"
                                                            valueText={
                                                                item.leadSource != null ? item.leadSource.name : ''
                                                            }
                                                            textColor=""
                                                            errorText="-Unknown-"
                                                            chipHeight={20}
                                                            chipFontSize="0.75rem"
                                                            chipfontWeight={500}
                                                            isChip={false}
                                                        />
                                                    </Box>
                                                </Grid>
                                                <Grid item xs={12}>
                                                    <Box sx={{ display: 'flex', mb: 0.7, alignItems: 'center' }}>
                                                        <LeadDataFormater
                                                            labelText="Street:"
                                                            valueText={
                                                                item.addressStreet != null ? item.addressStreet : ''
                                                            }
                                                            textColor=""
                                                            errorText="-Unknown-"
                                                            chipHeight={20}
                                                            chipFontSize="0.75rem"
                                                            chipfontWeight={500}
                                                            isChip={false}
                                                        />
                                                    </Box>
                                                </Grid>
                                                <Grid item xs={6}>
                                                    <Box sx={{ display: 'flex', mb: 0.7, alignItems: 'center' }}>
                                                        <LeadDataFormater
                                                            labelText="City:"
                                                            valueText={item.addressCity != null ? item.addressCity : ''}
                                                            textColor=""
                                                            errorText="-City is missing-"
                                                            chipHeight={20}
                                                            chipFontSize="0.75rem"
                                                            chipfontWeight={500}
                                                            isChip={false}
                                                        />
                                                    </Box>
                                                </Grid>
                                                <Grid item xs={6}>
                                                    <Box sx={{ display: 'flex', mb: 0.7, alignItems: 'center' }}>
                                                        <LeadDataFormater
                                                            labelText="Province/State:"
                                                            valueText={
                                                                item.addressProvince != null ? item.addressProvince : ''
                                                            }
                                                            textColor=""
                                                            errorText="-Unknown-"
                                                            chipHeight={20}
                                                            chipFontSize="0.75rem"
                                                            chipfontWeight={500}
                                                            isChip={false}
                                                        />
                                                    </Box>
                                                </Grid>
                                                <Grid item xs={6}>
                                                    <Box sx={{ display: 'flex', mb: 0.7, alignItems: 'center' }}>
                                                        <LeadDataFormater
                                                            labelText="Postal Code:"
                                                            valueText={
                                                                item.addressPostalCode != null
                                                                    ? item.addressPostalCode
                                                                    : ''
                                                            }
                                                            textColor=""
                                                            errorText="-Unknown-"
                                                            chipHeight={20}
                                                            chipFontSize="0.75rem"
                                                            chipfontWeight={500}
                                                            isChip={false}
                                                        />
                                                    </Box>
                                                </Grid>
                                                <Grid item xs={6}>
                                                    <Box sx={{ display: 'flex', mb: 0.7, alignItems: 'center' }}>
                                                        <LeadDataFormater
                                                            labelText="Country:"
                                                            valueText={item.country != null ? item.country.name : ''}
                                                            textColor=""
                                                            errorText="-Unknown-"
                                                            chipHeight={20}
                                                            chipFontSize="0.75rem"
                                                            chipfontWeight={500}
                                                            isChip={false}
                                                        />
                                                    </Box>
                                                </Grid>
                                            </Grid>
                                            <Grid item xs={6}>
                                                <Box sx={{ display: 'flex', mb: 0.7, alignItems: 'center' }}>
                                                    <LeadDataFormater
                                                        labelText="Region:"
                                                        valueText={
                                                            item.addressRegion != null ? item.addressRegion.name : ''
                                                        }
                                                        textColor=""
                                                        errorText="-Unknown-"
                                                        chipHeight={20}
                                                        chipFontSize="0.75rem"
                                                        chipfontWeight={500}
                                                        isChip={false}
                                                    />
                                                </Box>
                                            </Grid>
                                            <Grid item xs={6}>
                                                <Box sx={{ display: 'flex', mb: 0.7, alignItems: 'center' }}>
                                                    <LeadDataFormater
                                                        labelText="Customer:"
                                                        valueText={item.customer != null ? item.customer.name : ''}
                                                        textColor=""
                                                        errorText="-Unknown-"
                                                        chipHeight={20}
                                                        chipFontSize="0.75rem"
                                                        chipfontWeight={500}
                                                        isChip={false}
                                                    />
                                                </Box>
                                            </Grid>

                                            {item.referralFrom && (
                                                <Grid item xs={12}>
                                                    <Box sx={{ display: 'flex', mb: 0.7, alignItems: 'center' }}>
                                                        <LeadDataFormater
                                                            labelText="Referral From:"
                                                            valueText={item.referralFrom.fullName}
                                                            textColor=""
                                                            errorText="-Unknown-"
                                                            chipHeight={20}
                                                            chipFontSize="0.75rem"
                                                            chipfontWeight={500}
                                                            isChip={false}
                                                        />
                                                    </Box>
                                                </Grid>
                                            )}
                                            {item.referralTo && (
                                                <Grid item xs={12}>
                                                    <Box sx={{ display: 'flex', mb: 0.7, alignItems: 'center' }}>
                                                        <LeadDataFormater
                                                            labelText="Referral To:"
                                                            valueText={item.referralTo.fullName}
                                                            textColor=""
                                                            errorText="-Unknown-"
                                                            chipHeight={20}
                                                            chipFontSize="0.75rem"
                                                            chipfontWeight={500}
                                                            isChip={false}
                                                        />
                                                    </Box>
                                                </Grid>
                                            )}
                                        </Box>
                                    </AccordionDetails>
                                </Accordion>
                            </CardContent>
                            <CardContent></CardContent>
                            {!quickLeadDialogMode && (
                                <CardActions sx={{ display: 'flex', justifyContent: 'right' }}>
                                    <Grid container spacing={2} justifyContent={'center'}>
                                        <Grid item xs={10}>
                                            <RenderIf claimType={ClaimType.Leads} claimValue={ClaimValue.Edit}>
                                                <Tooltip title="Edit Lead">
                                                    <IconButton
                                                        aria-label="Edit Lead"
                                                        color="info"
                                                        size="small"
                                                        onClick={onEditLead}
                                                    >
                                                        <EditIcon />
                                                    </IconButton>
                                                </Tooltip>
                                            </RenderIf>
                                            <RenderIf claimType={ClaimType.Leads} claimValue={ClaimValue.ViewNotes}>
                                                <Tooltip title="View Notes">
                                                    <IconButton
                                                        aria-label="Notes"
                                                        color="info"
                                                        size="small"
                                                        onClick={() =>
                                                            LeadScreenStore.update((s) => {
                                                                (s.selectedView = ViewType.Notes),
                                                                    (s.selectedLead = item);
                                                            })
                                                        }
                                                    >
                                                        <NoteIcon />
                                                    </IconButton>
                                                </Tooltip>
                                            </RenderIf>
                                            <Tooltip title="Analytics">
                                                <IconButton aria-label="Analytics" color="info" disabled>
                                                    <AnalyticsIcon />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Collaborate">
                                                <IconButton aria-label="Chatter" color="info" disabled>
                                                    <GroupsIcon />
                                                </IconButton>
                                            </Tooltip>
                                            <RenderIf claimType={ClaimType.Leads} claimValue={ClaimValue.ViewTasks}>
                                                <Tooltip title="Tasks">
                                                    <IconButton
                                                        aria-label="Tasks"
                                                        color="info"
                                                        onClick={() =>
                                                            LeadScreenStore.update((s) => {
                                                                (s.selectedView = ViewType.Tasks),
                                                                    (s.selectedLead = item);
                                                            })
                                                        }
                                                    >
                                                        <AssignmentTurnedInIcon />
                                                    </IconButton>
                                                </Tooltip>
                                            </RenderIf>
                                            <Tooltip title="Meeting">
                                                <IconButton aria-label="Meeting" color="info" disabled>
                                                    <CalendarMonthIcon />
                                                </IconButton>
                                            </Tooltip>
                                            <RenderIf claimType={ClaimType.Leads} claimValue={ClaimValue.Delete}>
                                                <Tooltip title="Delete">
                                                    <IconButton aria-label="Tasks" color="error" onClick={onDeleteLead}>
                                                        <DeleteTwoToneIcon />
                                                    </IconButton>
                                                </Tooltip>
                                            </RenderIf>
                                        </Grid>
                                        <Grid item xs={2} sx={{ display: 'flex', alignItems: 'right' }}>
                                            <Box sx={{ display: 'flex', alignItems: 'right' }}>
                                                <RenderIf
                                                    claimType={ClaimType.Leads}
                                                    claimValue={ClaimValue.OverrideWorkflow}
                                                >
                                                    <Box>
                                                        <Tooltip title="Override Activity">
                                                            <IconButton
                                                                aria-label="Override Activity"
                                                                onClick={handleClick}
                                                                aria-controls={open ? 'override-menu' : undefined}
                                                                aria-haspopup="true"
                                                                aria-expanded={open ? 'true' : undefined}
                                                            >
                                                                <MoreVertIcon />
                                                            </IconButton>
                                                        </Tooltip>
                                                        <Menu
                                                            anchorEl={anchorEl}
                                                            id="override-menu"
                                                            open={open}
                                                            onClose={() => handleClose('')}
                                                            onClick={() => handleClose('')}
                                                            PaperProps={{
                                                                elevation: 0,
                                                                sx: {
                                                                    overflow: 'visible',
                                                                    filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
                                                                    mt: 1.5,
                                                                    '& .MuiAvatar-root': {
                                                                        width: 32,
                                                                        height: 32,
                                                                        ml: -0.5,
                                                                        mr: 1,
                                                                    },
                                                                    '&:before': {
                                                                        content: '""',
                                                                        display: 'block',
                                                                        position: 'absolute',
                                                                        top: 0,
                                                                        right: 14,
                                                                        width: 10,
                                                                        height: 10,
                                                                        bgcolor: 'background.paper',
                                                                        transform: 'translateY(-50%) rotate(45deg)',
                                                                        zIndex: 0,
                                                                    },
                                                                },
                                                            }}
                                                            transformOrigin={{
                                                                horizontal: 'right',
                                                                vertical: 'top',
                                                            }}
                                                            anchorOrigin={{
                                                                horizontal: 'right',
                                                                vertical: 'bottom',
                                                            }}
                                                        >
                                                            <RenderIf
                                                                claimType={ClaimType.Leads}
                                                                claimValue={ClaimValue.ChangeActivity}
                                                            >
                                                                <MenuItem onClick={() => handleClose('ACTIVITY')}>
                                                                    <ListItemIcon>
                                                                        <ChangeCircleIcon />
                                                                    </ListItemIcon>
                                                                    Change Activity
                                                                </MenuItem>
                                                            </RenderIf>
                                                            <RenderIf
                                                                claimType={ClaimType.Leads}
                                                                claimValue={ClaimValue.ChangeCadence}
                                                            >
                                                                <MenuItem onClick={() => handleClose('CADENCE')}>
                                                                    <ListItemIcon>
                                                                        <DisplaySettingsIcon />
                                                                    </ListItemIcon>
                                                                    Change Cadence
                                                                </MenuItem>
                                                            </RenderIf>
                                                            <RenderIf
                                                                claimType={ClaimType.Leads}
                                                                claimValue={ClaimValue.ChangeSprint}
                                                            >
                                                                <MenuItem onClick={() => handleClose('SPRINT')}>
                                                                    <ListItemIcon>
                                                                        <ManageHistoryIcon />
                                                                    </ListItemIcon>
                                                                    Change Sprint
                                                                </MenuItem>
                                                            </RenderIf>
                                                            <RenderIf
                                                                claimType={ClaimType.Leads}
                                                                claimValue={ClaimValue.ChangeOwner}
                                                            >
                                                                <MenuItem onClick={() => handleClose('OWNER')}>
                                                                    <ListItemIcon>
                                                                        <ManageAccountsIcon />
                                                                    </ListItemIcon>
                                                                    Change Owner
                                                                </MenuItem>
                                                            </RenderIf>
                                                            <RenderIf
                                                                claimType={ClaimType.Leads}
                                                                claimValue={ClaimValue.ChangeActivityDate}
                                                            >
                                                                <MenuItem onClick={() => handleClose('DATE')}>
                                                                    <ListItemIcon>
                                                                        <DateRangeIcon />
                                                                    </ListItemIcon>
                                                                    Change Activity Date
                                                                </MenuItem>
                                                            </RenderIf>
                                                        </Menu>
                                                    </Box>
                                                </RenderIf>
                                                <RenderIf
                                                    claimType={ClaimType.Leads}
                                                    claimValue={ClaimValue.ExecuteActivities}
                                                >
                                                    {item.nextActivity &&
                                                        item.nextActivity.name != '' &&
                                                        item.nextActivityDateTime &&
                                                        ActivityReady(item.nextActivityDateTime) < 1 && (
                                                            <Tooltip title={'Activity due:' + item.nextActivity.name}>
                                                                <IconButton
                                                                    aria-label="Next activity"
                                                                    onClick={onExecuteActivity}
                                                                >
                                                                    <Icon
                                                                        icon={item.nextActivity.icon}
                                                                        sx={{
                                                                            color: ActivityColor(
                                                                                item.nextActivityDateTime,
                                                                            ),
                                                                        }}
                                                                    ></Icon>
                                                                </IconButton>
                                                            </Tooltip>
                                                        )}
                                                    {item.nextActivity &&
                                                        item.nextActivity.name != '' &&
                                                        item.nextActivityDateTime &&
                                                        ActivityReady(item.nextActivityDateTime) > 0 && (
                                                            <Tooltip title={'Activity due:' + item.nextActivity.name}>
                                                                <IconButton aria-label="Next activity" disabled>
                                                                    <Icon
                                                                        icon={item.nextActivity.icon}
                                                                        sx={{
                                                                            color: ActivityColor(
                                                                                item.nextActivityDateTime,
                                                                            ),
                                                                        }}
                                                                    ></Icon>
                                                                </IconButton>
                                                            </Tooltip>
                                                        )}
                                                </RenderIf>
                                            </Box>
                                        </Grid>
                                    </Grid>

                                    {/*<Button variant='outlined' sx={{ mr: 3 }} onClick={() => LeadScreenStore.update(s => { s.selectedView = ViewType.AccountDetails, s.selectedLead = item })}>*/}
                                    {/*    Account*/}
                                    {/*</Button>*/}
                                </CardActions>
                            )}
                        </Card>
                    </Grid>
                </Grid>
            </Box>
        );
    } else {
        return null;
    }
};

export { LeadSummaryView };
export default LeadSummaryView;
